import * as React from 'react'

import { Layer, Source, useMap } from 'react-map-gl/mapbox'

import { SitePermitStatus } from '~/client/graph'

import {
  getWorkflowPinImageKey,
  registerMissingWorkflowPinImage,
} from '../utils/GLWorkflowPinImageGenerator'

interface IProps {
  sourceId: string
  layerId: string
  latitude: number
  longitude: number
  iconName?: string
  isAssigned?: boolean
  status?: SitePermitStatus
  isLate?: boolean
}

/**
 * Shares the map's own pin image by id, so the star, shadow, stroke and status fill cannot drift
 * from it — and unlike a DOM `<Marker>` it survives the `getCanvas()` capture a report is read from.
 */
function GLWorkflowPinMarker({
  sourceId,
  layerId,
  latitude,
  longitude,
  iconName,
  isAssigned,
  status,
  isLate,
}: IProps): JSX.Element {
  const { current: map } = useMap()

  const imageKey = getWorkflowPinImageKey({
    iconName: iconName ?? '',
    withCount: false,
    isSelected: false,
    isAssigned: !!isAssigned,
    status,
    isLate,
  })

  // styleimagemissing is a one-shot per id and a single pin places once, so a raster that landed
  // late would leave the symbol dropped for the session. Registering up front closes that window;
  // the store's handler stays as the style-reload fallback.
  React.useEffect(() => {
    const gl = map?.getMap()
    if (gl) registerMissingWorkflowPinImage(gl, imageKey)
  }, [map, imageKey])

  const data = React.useMemo(
    () =>
      ({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: [longitude, latitude] },
          },
        ],
      }) as GeoJSON.FeatureCollection,
    [longitude, latitude],
  )

  // Memoised on the one value that varies: react-map-gl only diffs a layer's layout when the
  // object identity changed, so an inline one costs a deep walk of every key per render.
  const layout = React.useMemo(
    () => ({
      'icon-image': imageKey,
      // The raster puts the tip on the bottom edge.
      'icon-anchor': 'bottom',
      'icon-size': 1,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    }),
    [imageKey],
  )

  return (
    <Source id={sourceId} type="geojson" data={data as never}>
      <Layer id={layerId} type="symbol" layout={layout as never} />
    </Source>
  )
}

export default React.memo(GLWorkflowPinMarker)
